import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'palette/Button';
import { Icon } from 'palette/Icon';
import {
    Modal, ModalBox, ModalBoxBody, ModalBoxHeader, useModal,
} from 'palette/Modal';
import { TextLink } from 'palette/TextLink';
import { BodyText, Small } from 'palette/Typography';
import { useConfig } from '@common/Hooks/useConfig';
import { BEMBlock } from '@helpers/classnames';
import { isWebshopMobile } from '@helpers/env';
import { SPONSORED_PRODUCT_MODAL_ID } from './constants/modalIds';
import { MODAL_POSITION } from './constants/modalPosition';
import './styles/SponsoredProductsModal.scss';

const MINIMUM_DISTANCE_TO_BOTTOM = 230;

interface SponsoredProductsModalProps {
    id: keyof typeof SPONSORED_PRODUCT_MODAL_ID;
    hasSplash?: boolean;
    className?: string;
    title: string;
    message: string;
    modalClassName?: string;
    splash?: string;
    linkURL: string;
    linkLabel: string;
}

export function SponsoredProductsModal({
    id,
    hasSplash,
    className,
    title,
    message,
    modalClassName,
    splash,
    linkURL,
    linkLabel,
}: SponsoredProductsModalProps) {
    const iconRef = useRef<HTMLButtonElement>(null);
    const { activeModalId, setActiveModalId } = useModal();
    const config = useConfig();
    const isMobileView = isWebshopMobile(config);
    const [modalPosition, setModalPosition] = useState(MODAL_POSITION.bellow);
    const isModalAboveIcon = modalPosition === MODAL_POSITION.above && !isMobileView;
    const [block, BEMElement] = BEMBlock('sponsored-products-modal');

    const handleClick = () => {
        setActiveModalId(id);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (iconRef.current) {
                const iconRect = iconRef.current.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const distanceToBottom = windowHeight - (iconRect.top + iconRect.height);

                if (distanceToBottom > MINIMUM_DISTANCE_TO_BOTTOM) {
                    setModalPosition(MODAL_POSITION.bellow);
                } else {
                    setModalPosition(MODAL_POSITION.above);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [modalPosition]);

    return (
        <div className={classNames(block, className)}>
            <Button
                ref={iconRef}
                isTransparent
                className={BEMElement('button')}
                shape={hasSplash ? 'rectangle' : 'round'}
                size="small"
                theme="secondary"
                onClick={handleClick}
            >
                {hasSplash && (
                    <Small
                        className={BEMElement('splash')}
                        color="light"
                    >
                        {splash}
                    </Small>
                )}
                <Icon
                    className={BEMElement('icon')}
                    name="palette_info"
                    size="xx-small"
                />
            </Button>
            {activeModalId === id && (
                <ModalBox
                    className={classNames(BEMElement('modal'), modalClassName, {
                        [`${BEMElement('modal')}--mobile`]: isMobileView,
                        [`${BEMElement('modal')}--above`]: isModalAboveIcon,
                    })}
                    isActive={activeModalId === id}
                >
                    <>
                        <ModalBoxHeader title={title} />
                        <ModalBoxBody>
                            <BodyText>
                                {message}
                                <TextLink
                                    className={BEMElement('text-link', { desktop: !isMobileView })}
                                    href={linkURL}
                                >
                                    {linkLabel}
                                </TextLink>
                            </BodyText>
                        </ModalBoxBody>
                    </>
                </ModalBox>
            )}
            <Modal id={id} />
        </div>
    );
}
